/**
 * Platform-admin gate for the web app's `/admin/*` area (a sibling layout of
 * `_agents`, without its org/onboarding gate or ChatProvider).
 *
 * `_auth` already confirmed a live session; this layer confirms platform-admin
 * status via `GET /admin/me`. A 403 (NotPlatformAdminError) is a real user who
 * simply isn't staff — sign them out and bounce to sign-in rather than leaving
 * them in a half-loaded admin shell. The parent authenticated layout renders the shared rail:
 * all-organization study management (`/admin/studies`, reusing the workspace's
 * study pages, staff working directly as themselves) and workspace management.
 */

import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { supabase } from "@lato/client/lib/supabase";
import { adminMeQueryOptions, NotPlatformAdminError } from "@/hooks/adminMe";
import { ChatProvider } from "@/chat/ChatProvider";
import { webChatConfig } from "@/chat-config";
import { useMe } from "@/hooks/useCloudChat";

/** The chat provider the shared study pages need (study chats run as the
 *  signed-in admin; each conversation is pinned to its study's org server-side). */
function AdminLayout() {
  const { data: me } = useMe();
  return (
    <ChatProvider config={webChatConfig(me?.organization?.id ?? "")}>
      <Outlet />
    </ChatProvider>
  );
}

export const Route = createFileRoute("/_auth/admin")({
  component: AdminLayout,
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData(adminMeQueryOptions);
      return { me };
    } catch (err) {
      if (err instanceof NotPlatformAdminError) {
        await supabase.auth.signOut();
        throw redirect({ to: "/signin", replace: true, search: { redirect: "" } });
      }
      throw err;
    }
  },
});
